import type { AgenticLogicRulePublishJobDto } from "@intelligems/ig-types";
import { IgAlert } from "@intelligems/ig-theme";

interface TemplatePublishStatusProps {
  job: AgenticLogicRulePublishJobDto;
  /**
   * `true` when this session started or watched the run. A success this
   * session did not run is history hydrated from rule detail — announcing it
   * would make "Rule version published" permanent chrome on every published
   * template rule. A failure stays reported either way: it is still actionable.
   */
  isSessionRun: boolean;
  /**
   * The org's Advanced verdict, passed in rather than read from a hook: this is
   * a pure alert, and reaching for the org config here would drag a redux
   * provider into every render of it.
   */
  advancedEnabled: boolean;
}

const failedPublishMessage = (job: AgenticLogicRulePublishJobDto): string => {
  const result = job.gates.find((gate) => gate.status === "failed")?.result;
  const recovery = "Fix the configuration and save it again to retry.";
  return result ? `${result} ${recovery}` : recovery;
};

/**
 * Same three moments whatever the org sees — only the words differ. A merchant
 * without the Advanced toggle has no version number and no publish verb, so
 * these report the RULE passing its checks rather than a VERSION being published.
 */
export const TemplatePublishStatus = ({
  job,
  isSessionRun,
  advancedEnabled,
}: TemplatePublishStatusProps) => {
  if (job.status === "failed") {
    return (
      <IgAlert
        alertKey={`agentic-logic-template-publish-failed-${job.id}`}
        severity="error"
        title={
          advancedEnabled
            ? "Rule version could not publish"
            : "This rule didn't pass all checks"
        }
        message={failedPublishMessage(job)}
      />
    );
  }

  if (job.status === "succeeded") {
    if (!isSessionRun) {
      return null;
    }
    return (
      <IgAlert
        alertKey={`agentic-logic-template-publish-succeeded-${job.id}`}
        severity="success"
        title={
          advancedEnabled
            ? "Rule version published"
            : "This rule has passed all checks"
        }
        message="The rule is ready to assign to a compatible placement."
      />
    );
  }

  return (
    <IgAlert
      alertKey={`agentic-logic-template-publishing-${job.id}`}
      severity="info"
      title={advancedEnabled ? "Publishing rule version" : "Checking this rule"}
      message={
        advancedEnabled
          ? "The rule will be ready to assign after all publish checks pass."
          : "We're checking this rule. It's ready to assign once the checks pass."
      }
    />
  );
};
